import { EventEmitter, Injectable } from '@angular/core';
import {
  BANNER,
  BOOK_DEMO,
  COMPANIES,
  FEATURES,
  REFERENCE_STORIES,
  STATISTICS,
  USPS,
} from '@constants/app.constants';
import { DEFAULT_CONTENT_MAP } from '@constants/content-map.constant';
import { OV_DEFAULT_ID } from '@constants/general.constants';
import { BookDemoSectionData } from '@models/app.models';

@Injectable({ providedIn: 'root' })
export class ContentService {
  bookDemoSectionDataLoaded = new EventEmitter<BookDemoSectionData>();

  getBanner(company: string) {
    return BANNER[company.toLowerCase()] ?? BANNER['calenso'];
  }

  getDefsOrCustoms(customs: string[] = [],defaults: string[] = []): string[] {
    if (customs?.length) {
      return [...customs];
    }

    return [...defaults];
  }

  getCustomers(site: string, ids: string[] = []) {
    if (!site || !DEFAULT_CONTENT_MAP[site]) return [];

    const def = DEFAULT_CONTENT_MAP[site]?.companies ?? [];
    const merged = this.getDefsOrCustoms(ids, def);
    return merged.map((id) => COMPANIES[id]).filter(Boolean);
  }

  getFeatures(site: string, ids: string[] = []) {
    if (!site || !DEFAULT_CONTENT_MAP[site]) return [];

    const def = DEFAULT_CONTENT_MAP[site]?.features ?? [];
    const merged = this.getDefsOrCustoms(ids, def);
    return merged.map((id) => FEATURES.find((f) => f.identifier === id)).filter(Boolean);
  }

  getUsps(site: string, ids: string[] = []) {
    if (!site || !DEFAULT_CONTENT_MAP[site]) return [];

    const def = DEFAULT_CONTENT_MAP[site]?.usp ?? [];
    const merged = this.getDefsOrCustoms(ids, def);
    return merged
      .map((id, index) => {
        const item = USPS[id];
        return item ? { ...item, reverse: index % 2 !== 0 } : null;
      })
      .filter(Boolean);
  }

  getStatistics(site: string, ids: string[] = []) {
    if (!site || !DEFAULT_CONTENT_MAP[site]) return [];

    const def = DEFAULT_CONTENT_MAP[site]?.statistics ?? [];
    const merged = this.getDefsOrCustoms(ids, def);
    return merged.map((id) => STATISTICS[id]).filter(Boolean);
  }

  getReferenceStories(site: string, ids: string[] = []) {
    if (!site || !DEFAULT_CONTENT_MAP[site]) return [];

    const def = DEFAULT_CONTENT_MAP[site]?.ref_stories ?? [];
    const merged = this.getDefsOrCustoms(ids, def);
    return merged.map((id) => REFERENCE_STORIES[id]).filter(Boolean);
  }

  getReferenceStoriesWithHighlight(site: string, all: string[] = []) {
    const stories = this.getReferenceStories(site, all);
    if (!stories.length) return [];

    const highlightId = stories[0]?.identifier;

    const updatedStories = stories.map((story) => ({
      ...story,
      detailedView: story.identifier === highlightId,
    }));

    return updatedStories;
  }

  getBookDemo(worker: string) {
    return BOOK_DEMO[worker] ?? BOOK_DEMO['ralf'] ?? null;
  }

  getOverviewVideoId(id: string): string {
    return id || OV_DEFAULT_ID;
  }
}
